import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Carousel } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPhone, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const CLIENT_PHONE_NUMBER = '918148111516';
const TARIFF_VISIBLE = 3;

const Tariff = () => {
  const navigate = useNavigate();
  const [tariffIndex, setTariffIndex] = useState(0);
  const [tariffTransition, setTariffTransition] = useState(true);
  const [tariffStepPx, setTariffStepPx] = useState(0);
  const [tariffCardWidthPx, setTariffCardWidthPx] = useState(0);
  const tariffLockRef = useRef(false);
  const tariffViewportRef = useRef(null);

  const letterStyle = {
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: '600',
    letterSpacing: '-0.02em',
    lineHeight: '1.4'
  };

  const headingStyle = {
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: '700',
    letterSpacing: '-0.03em',
    lineHeight: '1.3'
  };

  const cars = useMemo(() => [
      {
        name: "SEDAN",
        displayName: "Sedan",
        oneWayRate: 15,
        roundTripRate: 14,
        capacity: "4+1",
        ac: "Yes",
        driverBata: 400,
        hillCharges: 300,
        permitCharge: 14,
        images: [
          {
            url: "/images/sedan/1.jpg",
            model: "TATA ZEST",
            bgColor: "#FFD700",
          },
          {
            url: "/images/sedan/2.jpg",
            model: "SWIFT DZIRE",
            bgColor: "#FFD700",
          },
          {
            url: "/images/sedan/3.jpg",
            model: "HYNDAI AURA",
            bgColor: "#FFD700",
          },
        ],
      },
      {
        name: "ETIOS",
        displayName: "Etios",
        oneWayRate: 16,
        roundTripRate: 15,
        capacity: "4+1",
        ac: "Yes",
        driverBata: 400,
        hillCharges: 300,
        permitCharge: 14,
        images: [
          {
            url: "/images/sedan2/1.jpg",
            model: "NISSIAN SUNNY",
            bgColor: "#FFD700",
          },
          {
            url: "/images/sedan2/2.jpg",
            model: "TOYOTA ",
            bgColor: "#FFD700",
          },
          {
            url: "/images/sedan2/3.jpg",
            model: "MARUTI CIAZ",
            bgColor: "#FFD700",
          },
        ],
      },
      {
        name: "MUV",
        displayName: "MUV",
        oneWayRate: 20,
        roundTripRate: 19,
        capacity: "6+1 / 7+1",
        ac: "Yes",
        driverBata: 400,
        hillCharges: 300,
        permitCharge: 14,
        images: [
          { url: "/images/suv/xylo-1.jpg", model: "XYLO", bgColor: "#FFD700" },
          {
            url: "/images/suv/SUV.jpg",
            model: "MARUTI ERTIGA",
            bgColor: "#FFD700",
          },
          {
            url: "/images/suv/SUV-1.jpg",
            model: "MARUTI ERTIGA",
            bgColor: "#FFD700",
          },
          {
            url: "/images/suv/marazzo-1.jpg",
            model: "MARAZZO",
            bgColor: "#FFD700",
          },
        ],
      },
      {
        name: "INNOVA",
        displayName: "Innova",
        oneWayRate: 21,
        roundTripRate: 20,
        capacity: "6+1 / 7+1",
        ac: "Yes",
        driverBata: 400,
        hillCharges: 300,
        permitCharge: 14,
        images: [
          {
            url: "/images/innova/INNOVA.jpg",
            model: "VEHICLE",
            bgColor: "#FFD700",
          },
        ],
      },
      {
        name: "CRYSTA",
        displayName: "Crysta",
        oneWayRate: 24,
        roundTripRate: 23,
        capacity: "6+1 / 7+1",
        ac: "Yes",
        driverBata: 500,
        hillCharges: 300,
        permitCharge: 14,
        images: [
          {
            url: "/images/crysta/CRYSTA.jpg",
            model: "INNOVA CRYSTA",
            bgColor: "#FFD700",
          },
        ],
      },
  ], []);

  const extendedCars = useMemo(
    () => [...cars, ...cars.slice(0, TARIFF_VISIBLE)],
    [cars]
  );

  const scrollTariffCards = (direction) => {
    if (tariffLockRef.current || cars.length === 0) return;
    tariffLockRef.current = true;
    const total = cars.length;

    if (direction < 0 && tariffIndex === 0) {
      setTariffTransition(false);
      setTariffIndex(total);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTariffTransition(true);
          setTariffIndex(total - 1);
        });
      });
      return;
    }

    setTariffTransition(true);
    setTariffIndex((prev) => prev + direction);
  };

  useEffect(() => {
    if (!cars.length) return undefined;
    const total = cars.length;

    if (tariffIndex < total) {
      const unlockTimer = setTimeout(() => {
        tariffLockRef.current = false;
      }, 480);
      return () => clearTimeout(unlockTimer);
    }

    const resetTimer = setTimeout(() => {
      setTariffTransition(false);
      setTariffIndex(0);
      requestAnimationFrame(() => {
        tariffLockRef.current = false;
      });
    }, 480);

    return () => clearTimeout(resetTimer);
  }, [tariffIndex, cars.length]);

  useEffect(() => {
    const measureTariffStep = () => {
      const viewport = tariffViewportRef.current;
      if (!viewport) return;
      const gap = 16;
      const cardWidth = (viewport.clientWidth - gap * 2) / 3;
      setTariffCardWidthPx(cardWidth);
      setTariffStepPx(cardWidth + gap);
    };

    measureTariffStep();
    window.addEventListener('resize', measureTariffStep);
    return () => window.removeEventListener('resize', measureTariffStep);
  }, [extendedCars.length]);

  const renderCard = (car) => (
    <Card
      className="border-0 tariff-match-card w-100"
      style={{
        borderRadius: '18px',
        overflow: 'hidden',
        background: '#ffffff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
      }}
    >
      <div className="position-relative tariff-match-image-wrap">
        <Carousel
          interval={3500}
          indicators={car.images.length > 1}
          controls={false}
          pause="hover"
          className="tariff-match-carousel"
        >
          {car.images.map((imageObj, imgIndex) => (
            <Carousel.Item key={imgIndex}>
              <div className="tariff-match-image-box">
                <img
                  src={imageObj.url}
                  alt={`${car.name} - ${imageObj.model}`}
                  className="tariff-match-image"
                  loading="lazy"
                />
              </div>
            </Carousel.Item>
          ))}
        </Carousel>

      </div>

      <Card.Body className="tariff-match-body">
        <h3 className="tariff-match-title">{car.displayName}</h3>

        <div className="tariff-match-rates">
          <div className="tariff-match-rate-box">
            <span className="tariff-match-rate-label">One Way Trip</span>
            <span className="tariff-match-rate-oneway">₹{car.oneWayRate}/km</span>
          </div>
          <div className="tariff-match-rate-box">
            <span className="tariff-match-rate-label">Round Trip</span>
            <span className="tariff-match-rate-round">₹{car.roundTripRate}/km</span>
          </div>
        </div>

        <div className="tariff-match-specs">
          <div className="tariff-match-spec">
            <span className="tariff-match-spec-label">Capacity</span>
            <span className="tariff-match-spec-value">{car.capacity}</span>
          </div>
          <div className="tariff-match-spec">
            <span className="tariff-match-spec-label">AC</span>
            <span className="tariff-match-spec-value">{car.ac || 'Yes'}</span>
          </div>
          <div className="tariff-match-spec">
            <span className="tariff-match-spec-label">Driver Beta</span>
            <span className="tariff-match-spec-value">₹{car.driverBata}</span>
          </div>
        </div>

        <div className="tariff-match-actions">
          <button
            type="button"
            className="tariff-match-btn-book"
            onClick={() => {
              navigate('/');
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
            }}
          >
            Book Now
          </button>
          <a
            href={`tel:+${CLIENT_PHONE_NUMBER}`}
            className="tariff-match-btn-call"
          >
            <FaPhone size={13} />
            +91 81481 11516
          </a>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div className="fade-in py-5">
      <Container>
        <h1 className="text-center mb-5" style={{ 
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          ...headingStyle
        }}>
          <span className="text-warning" style={headingStyle}>Outstation</span> Tariff
        </h1>

        <Row className="justify-content-center tariff-cards-grid">
          {cars.map((car, index) => (
            <Col lg={6} md={6} key={index} className="mb-5 d-flex">
              {renderCard(car)}
            </Col>
          ))}
        </Row>

        <div className="tariff-cards-slider-wrap">
          <button
            type="button"
            className="tariff-slider-btn tariff-slider-btn-prev"
            onClick={() => scrollTariffCards(-1)}
            aria-label="Previous cars"
          >
            <FaChevronLeft />
          </button>

          <div className="tariff-cards-viewport" ref={tariffViewportRef}>
            <div
              className={`tariff-cards-track${tariffTransition ? ' is-animating' : ''}`}
              style={{ transform: `translateX(-${tariffIndex * tariffStepPx}px)` }}
            >
              {extendedCars.map((car, index) => (
                <div
                  className="tariff-slide-item"
                  key={`track-${index}-${car.name}`}
                  style={
                    tariffCardWidthPx
                      ? {
                          flex: `0 0 ${tariffCardWidthPx}px`,
                          width: `${tariffCardWidthPx}px`,
                          maxWidth: `${tariffCardWidthPx}px`,
                          minWidth: `${tariffCardWidthPx}px`
                        }
                      : undefined
                  }
                >
                  {renderCard(car)}
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="tariff-slider-btn tariff-slider-btn-next"
            onClick={() => scrollTariffCards(1)}
            aria-label="Next cars"
          >
            <FaChevronRight />
          </button>
        </div>

        <Card className="border-0 shadow mt-4" style={{ borderRadius: '15px', backgroundColor: '#fff3cd' }}>
          <Card.Body className="p-4">
            <h5 className="fw-bold mb-4" style={{ color: '#856404', ...headingStyle }}>FOR CUSTOMER INFORMATION</h5>
            <Row>
              <Col md={6}>
                <ul className="list-unstyled">
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Toll fees, Inter-State Permit charges (if any) are extra.</span>
                  </li>
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Drop Trips - Driver Bata For Sedan Rs.400 & SUV Rs.500</span>
                  </li>
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Waiting Charges Rs.150 per hour</span>
                  </li>
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Drop Trips - Minimum running must be 130kms per day</span>
                  </li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="list-unstyled">
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Round Trips - Driver Bata Sedan Rs.400 & SUV Rs.500/- per day</span>
                  </li>
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Round Trips - Minimum running must be 250kms per day</span>
                  </li>
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>Hill station charges - Sedan Rs.300 & SUV Rs.300</span>
                  </li>
                  <li className="mb-3 d-flex" style={letterStyle}>
                    <span className="text-warning me-2 fw-bold">•</span>
                    <span>1 day means 1 calendar day (midnight to midnight)</span>
                  </li>
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="mt-5">
          <Col lg={6} className="mx-auto">
            <p className="text-center text-muted small" style={letterStyle}>
              * Rates are subject to change. Please confirm at the time of booking.
            </p>
          </Col>
        </Row>
      </Container>

      <style>{`
        .tariff-match-card {
          max-width: none;
          margin: 0 auto;
          height: 100%;
        }

        .tariff-match-image-wrap {
          background: #ffffff;
        }

        .tariff-match-image-box {
          width: 100%;
          height: 210px;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px 16px 28px;
        }

        .tariff-match-image {
          max-width: 100%;
          max-height: 180px;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .tariff-match-carousel .carousel-indicators {
          margin-bottom: 0.35rem;
        }

        .tariff-match-carousel .carousel-indicators [data-bs-target] {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #c0c0c0;
        }

        .tariff-match-body {
          padding: 1.1rem 1.15rem 1.25rem !important;
        }

        .tariff-match-title {
          text-align: center;
          color: #0a2540;
          font-size: 1.7rem;
          font-weight: 800;
          margin: 0 0 0.9rem;
          letter-spacing: -0.02em;
        }

        .tariff-match-rates {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 1rem;
        }

        .tariff-match-rate-box {
          background: #f0f0f0;
          border-radius: 10px;
          padding: 12px 10px;
          text-align: center;
        }

        .tariff-match-rate-label {
          display: block;
          color: #8a8a8a;
          font-size: 0.82rem;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .tariff-match-rate-oneway {
          display: block;
          color: #1e88e5;
          font-size: 1.25rem;
          font-weight: 800;
        }

        .tariff-match-rate-round {
          display: block;
          color: #43a047;
          font-size: 1.25rem;
          font-weight: 800;
        }

        .tariff-match-specs {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 6px;
          text-align: center;
          margin-bottom: 1.15rem;
        }

        .tariff-match-spec-label {
          display: block;
          color: #8a8a8a;
          font-size: 0.78rem;
          font-weight: 500;
          margin-bottom: 2px;
        }

        .tariff-match-spec-value {
          display: block;
          color: #0a2540;
          font-size: 0.86rem;
          font-weight: 700;
          line-height: 1.25;
          white-space: nowrap;
        }

        .tariff-match-actions {
          display: grid;
          gap: 10px;
        }

        .tariff-match-btn-book {
          width: 100%;
          border: none;
          border-radius: 10px;
          background: #1e88e5;
          color: #fff;
          font-weight: 700;
          font-size: 1rem;
          padding: 12px 14px;
          cursor: pointer;
        }

        .tariff-match-btn-book:hover {
          background: #1976d2;
        }

        .tariff-match-btn-call {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border-radius: 10px;
          background: #ffc107;
          color: #111;
          font-weight: 700;
          font-size: 0.98rem;
          padding: 12px 14px;
          text-decoration: none;
        }

        .tariff-match-btn-call:hover {
          background: #ffb300;
          color: #111;
        }

        .tariff-cards-slider-wrap {
          display: none;
        }

        @media (min-width: 992px) {
          .tariff-cards-grid {
            display: none !important;
          }

          .tariff-cards-slider-wrap {
            display: block;
            position: relative;
            padding: 0 48px;
            margin-bottom: 1.5rem;
          }

          .tariff-cards-viewport {
            overflow: hidden;
            padding: 8px 4px 16px;
            width: 100%;
          }

          .tariff-cards-track {
            display: flex;
            gap: 1rem;
            will-change: transform;
          }

          .tariff-cards-track.is-animating {
            transition: transform 0.45s ease;
          }

          .tariff-slide-item {
            box-sizing: border-box;
          }

          .tariff-slider-btn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            z-index: 5;
            width: 42px;
            height: 42px;
            border-radius: 50%;
            border: none;
            background: #1e88e5;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 14px rgba(0,0,0,0.2);
            cursor: pointer;
          }

          .tariff-slider-btn:hover {
            background: #1976d2;
          }

          .tariff-slider-btn-prev {
            left: 0;
          }

          .tariff-slider-btn-next {
            right: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Tariff;
